/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/register.scss';

import React, {FocusEventHandler, MouseEventHandler, useRef} from 'react';
import {ElfsightWidget} from 'react-elfsight-widget';
import {RegisterErrors, RegisterStep} from "../../../util/interfaces";
import User from "../../../models/userModel";
import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    Link,
    Loader,
    preventDefault
} from '@dvrd/dvr-controls';
import PasswordStrength from "../../password/passwordStrength";
import {BolmateButton, BolmateInput, BolmatePasswordInput} from '../../controls';
import {CredentialResponse, GoogleLogin} from '@react-oauth/google';
import Image from "../../misc/image";
import {LOGIN_ROUTE} from "../../../util/constants";

interface Props {
    onChangeUser: ChangeKeyFunction;
    onChangePassword: ChangeFunction;
    onChangeTerms: ChangeFunction;
    onChangeCompanyName: ChangeFunction;
    onSubmit: MouseEventHandler;
    onClickTrustPilot: MouseEventHandler;
    onResetError: (key: keyof RegisterErrors) => FocusEventHandler;
    onGoogleSuccess: (response: CredentialResponse) => void;
    onGoogleError: VoidFunction;
    onClickLogin: MouseEventHandler;
    onClickGoogle: MouseEventHandler;
    onChangeStep: (step: RegisterStep) => MouseEventHandler;
    user: User;
    password: string;
    loading: boolean;
    errors: RegisterErrors;
    termsAccepted: boolean;
    companyName: string;
    step: RegisterStep;
    usersEstimate: number;
    isInvited: boolean;
}

export default function Register(props: Props) {
    const {
        user, password, errors, onResetError, onChangePassword, onChangeCompanyName, onChangeTerms, onChangeUser, step,
        loading, onClickGoogle, onClickLogin, onGoogleSuccess, onGoogleError, termsAccepted, companyName, onSubmit,
        onClickTrustPilot, usersEstimate, isInvited, onChangeStep
    } = props;
    const header = useRef<HTMLDivElement>(null);

    function onClickTermsLabel() {
        onChangeTerms(!termsAccepted);
    }

    function renderGoogle() {
        return (
            <div onClick={onClickGoogle} className='google-btn-wrapper'>
                <GoogleLogin onSuccess={onGoogleSuccess} onError={onGoogleError} text='signup_with' context='signup'
                             width='400px'/>
            </div>
        );
    }

    function renderForm() {
        return (
            <form onSubmit={preventDefault} className='form'>
                <BolmateInput onChange={onChangeCompanyName} value={companyName} label='Bedrijfsnaam *'
                              error={errors.companyName} onFocus={onResetError('companyName')}
                              inputProps={{placeholder: 'Bedrijfsnaam'}} className='register-field'
                              disabled={isInvited} autoFocus/>
                <BolmateInput value={user.firstname} onChange={onChangeUser('firstname')} error={errors.firstname}
                              onFocus={onResetError('firstname')} label='Voornaam *'
                              inputProps={{placeholder: 'Voornaam'}} className='register-field'/>
                <BolmateInput value={user.lastname} onChange={onChangeUser('lastname')} error={errors.lastname}
                              onFocus={onResetError('lastname')} label='Achternaam *'
                              inputProps={{placeholder: 'Achternaam'}} className='register-field'/>
                <BolmateInput value={user.email} onChange={onChangeUser('email')} label='E-mailadres *'
                              error={errors.email} onFocus={onResetError('email')} className='register-field'
                              inputProps={{type: 'email', autoComplete: 'email', placeholder: 'E-mailadres'}}/>
                <BolmatePasswordInput value={password} onChange={onChangePassword} label='Wachtwoord *'
                                      inputProps={{autoComplete: 'new-password', placeholder: 'Wachtwoord'}}
                                      error={errors.password} onFocus={onResetError('password')}
                                      className='register-field'/>
                <PasswordStrength value={password}/>
                <div className='disclaimer-container'>
                    <Checkbox checked={termsAccepted} onCheck={onChangeTerms}/>
                    <label className='disclaimer' onClick={onClickTermsLabel}>Ik ga akkoord
                        met de <Link route='https://bolmate.nl/utility/algemene-voorwaarden/' target='_blank'>Algemene
                            voorwaarden</Link> , <Link route='https://bolmate.nl/privacy-statement/' target='_blank'>Privacy
                            statement</Link> en <Link route='https://bolmate.nl/verwerkingsovereenkomst/'
                                                      target='_blank'>Verwerkersovereenkomst</Link>.</label>
                </div>
            </form>
        )
    }

    function renderRegistrationChoice() {
        return (
            <>
                <div className='header-container'>
                    <div className='logo-header' ref={header}>
                        <Image name='logo' className='logo'/>
                    </div>
                    <h1 className='header-title'>Probeer 14 dagen gratis!</h1>

                    <label className='sub-title'>Stopt automatisch. Verlenging mogelijk. Geen betaalmethode
                        nodig.</label>
                </div>
                <div className='login-btns-container'>
                    {renderGoogle()}
                    <span className='or-label'>- of -</span>
                    <div className='btns-container'>
                        <BolmateButton onClick={onChangeStep(RegisterStep.FORM)} label='Aanmelden met e-mailadres'
                                       className='email-btn'
                                       disabled={loading}
                                       ornaments={{element: <AwesomeIcon name='envelope' prefix='far'/>}}
                                       ornamentClassName='email-icon'/>
                    </div>
                </div>
                <label className='bottom-text'>Heb je al een Bolmate account? <Link route={LOGIN_ROUTE}
                                                                                    onClick={onClickLogin}
                                                                                    underline>Inloggen</Link></label>
            </>
        )
    }

    function renderRegistrationForm() {
        return (
            <>
                {!isInvited && <div className='back-button' onClick={onChangeStep(RegisterStep.METHOD_CHOICE)}>
                    <AwesomeIcon name='arrow-left'/>
                </div>}
                <div className='header-container'>
                    <div className='logo-header' ref={header}>
                        <Image name='logo' alt='Bolmate' className='logo'/>
                    </div>
                </div>
                <div className='content'>
                    {renderForm()}
                    <div className='submit-button'>
                        <BolmateButton onClick={onSubmit} label='Maak een account aan' className='submit-btn'
                                       disabled={loading || !termsAccepted}/>
                    </div>
                </div>
            </>
        )
    }

    return (
        <div className='register'>
            <div className='register-first'>
                <Image name='registrationBG' alt='Bolmate' className='background'/>
                <div className='content-left'>
                    <Loader active={loading}/>
                    {step === 0 ? renderRegistrationChoice() : renderRegistrationForm()}
                </div>
                <div className='content-right'>
                    <h1 className='header-title'>Meer dan {usersEstimate}+ bedrijven gebruiken Bolmate!</h1>
                    <Image name='logos-companies' alt='Bolmate' className='logo'/>
                    <div className='rating-container'>
                        <div className='google-container'>
                            <ElfsightWidget widgetId='1338afd7-4616-4fa9-8aa3-80cf163be59e' lazy modern/>
                        </div>
                        <div className='trustpilot-container'>
                            <Image name='trustpilot' alt='Trustpilot' className='img' onClick={onClickTrustPilot}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}